<template>
  <div class="setup-card">
    <div v-if="step === 'question-set'">
      <h2 class="card-title">Создание нового опроса</h2>

      <div class="form-group">
        <label class="form-label">Выберите набор вопросов:</label>
        <select v-model="selectedQuestionSetId" class="form-select">
          <option value="">-- Выберите --</option>
          <option v-for="qs in questionSets" :key="qs.id" :value="qs.id">
            {{ qs.name }}
          </option>
        </select>
        <p v-if="selectedQuestionSet" class="text-sm text-gray-600">
          {{ selectedQuestionSet.description }}
        </p>
      </div>

      <button
        @click="goToParticipants"
        :disabled="!selectedQuestionSetId || eligibleUsers.length < 2"
        class="btn-primary"
      >
        Далее → Выбрать участников
      </button>

      <div v-if="error" class="error-message">{{ error }}</div>
    </div>

    <div v-else-if="step === 'participants'">
      <div class="participants-header">
        <button class="link-btn" @click="step = 'question-set'">← Назад</button>
        <div>
          <h2 class="card-title no-margin">Выберите участников опроса</h2>
          <p class="subtitle">{{ selectedQuestionSet?.name }}</p>
        </div>
      </div>

      <div class="participants-toolbar">
        <div class="participants-count">Выбрано: {{ selectedUserIds.length }}</div>
        <div class="toolbar-actions">
          <button class="link-btn" @click="selectAll">Выбрать всех</button>
          <button class="link-btn" @click="deselectAll">Снять всех</button>
        </div>
      </div>

      <div class="participants-grid">
        <div
          v-for="user in eligibleUsers"
          :key="user.id"
          class="participant-card"
          :class="{ selected: isSelected(user.id) }"
          @click="toggle(user.id)"
        >
          <input
            type="checkbox"
            class="participant-check"
            :checked="isSelected(user.id)"
            @click.stop="toggle(user.id)"
          />
          <div class="participant-avatar">{{ initials(user.full_name) }}</div>
          <div class="participant-name">{{ user.full_name }}</div>
          <div v-if="user.rank" class="participant-rank">{{ user.rank }}</div>
        </div>
      </div>

      <div class="form-actions">
        <button
          @click="createSurvey"
          :disabled="selectedUserIds.length < 2 || loading"
          class="btn-primary"
        >
          <span v-if="!loading">Начать опрос</span>
          <span v-else>Загрузка...</span>
        </button>
      </div>

      <div v-if="error" class="error-message">{{ error }}</div>
    </div>
  </div>
</template>

<script>
import { getQuestionSets, startSurvey } from '@/api/mpo'
import { getUsers, ensureCurrentUser, getCachedCurrentUser } from '@/api/user'
import { logout } from '@/middlewares/auth'

export default {
  name: 'StarChartSetup',
  emits: ['survey-created'],
  data() {
    return {
      questionSets: [],
      availableUsers: [],
      selectedUserIds: [],
      selectedQuestionSetId: '',
      step: 'question-set',
      loading: false,
      error: null,
      currentUser: null,
    }
  },
  computed: {
    selectedQuestionSet() {
      return this.questionSets.find(qs => qs.id === parseInt(this.selectedQuestionSetId))
    },
    eligibleUsers() {
      const meId = this.currentUser?.id
      return this.availableUsers.filter(u =>
        !u.is_superuser && !u.is_staff && (meId == null || u.id !== meId)
      )
    },
  },
  mounted() {
    this.loadData()
  },
  methods: {
    async loadData() {
      try {
        this.questionSets = await getQuestionSets()
        try { this.currentUser = await ensureCurrentUser() } catch { this.currentUser = getCachedCurrentUser() }
        const usersResponse = await getUsers()
        this.availableUsers = usersResponse.data || usersResponse.results || usersResponse || []
        this.selectedUserIds = this.eligibleUsers.map(u => u.id)
      } catch (err) {
        this.error = 'Ошибка загрузки данных'
        console.error(err)
      }
    },
    goToParticipants() {
      this.step = 'participants'
      if (this.selectedUserIds.length === 0) {
        this.selectedUserIds = this.eligibleUsers.map(u => u.id)
      }
    },
    isSelected(id) {
      return this.selectedUserIds.includes(id)
    },
    toggle(id) {
      const idx = this.selectedUserIds.indexOf(id)
      if (idx === -1) this.selectedUserIds.push(id)
      else this.selectedUserIds.splice(idx, 1)
    },
    selectAll() {
      this.selectedUserIds = this.eligibleUsers.map(u => u.id)
    },
    deselectAll() {
      this.selectedUserIds = []
    },
    initials(fullName) {
      if (!fullName) return '—'
      const parts = String(fullName).trim().split(/\s+/)
      const a = parts[0]?.[0] || ''
      const b = parts[1]?.[0] || ''
      return (a + b).toUpperCase() || a.toUpperCase()
    },
    async createSurvey() {
      this.loading = true
      this.error = null

      if (this.selectedUserIds.length < 2) {
        this.error = 'Нужно выбрать минимум двух участников'
        this.loading = false
        return
      }

      const selectedSet = new Set(this.selectedUserIds)
      const participants = this.eligibleUsers
        .filter(u => selectedSet.has(u.id))
        .map(u => ({
          id: u.id,
          full_name: u.full_name,
          iin: u.iin,
          rank: u.rank,
          position: u.position,
        }))

      try {
        const survey = await startSurvey(
          parseInt(this.selectedQuestionSetId),
          participants
        )
        const token = survey?.kiosk_token
        if (!token) {
          throw new Error('Опрос создан, но не получен kiosk_token — обновите backend/фронт')
        }
        // Разлогиниваем администратора: kiosk-режим не должен видеть его сессию,
        // и следующий военнослужащий не сможет случайно попасть в админ-панель.
        try { logout() } catch { /* noop */ }
        // Переходим на публичный kiosk-роут в этом же окне.
        this.$router.replace({ name: 'mpo-kiosk', params: { token } })
      } catch (err) {
        this.error = err.response?.data?.error || err.message || 'Ошибка при создании опроса'
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.setup-card {
  background: white;
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.card-title {
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  color: #1a202c;
}

.card-title.no-margin {
  margin-bottom: 0.25rem;
}

.subtitle {
  font-size: 0.85rem;
  color: #6b7280;
  margin: 0 0 0.5rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-label {
  display: block;
  font-weight: 500;
  margin-bottom: 0.5rem;
  color: #374151;
}

.form-select,
.form-input {
  width: 100%;
  padding: 0.625rem;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 0.95rem;
}

.form-select:focus,
.form-input:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.participants-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.participants-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.participants-count {
  font-weight: 600;
  color: #1a202c;
}

.toolbar-actions {
  display: flex;
  gap: 1rem;
}

.link-btn {
  background: none;
  border: none;
  color: #2563eb;
  font-weight: 600;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  font-size: 0.9rem;
}

.link-btn:hover {
  text-decoration: underline;
}

.participants-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.participant-card {
  position: relative;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  padding: 1rem 0.75rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.15s ease;
  background: white;
}

.participant-card:hover {
  border-color: #93c5fd;
}

.participant-card.selected {
  border-color: #2563eb;
  background: #eff6ff;
}

.participant-check {
  position: absolute;
  top: 0.5rem;
  left: 0.5rem;
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.participant-avatar {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: #dbeafe;
  color: #1e40af;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.9rem;
  margin: 0.25rem auto 0.75rem;
}

.participant-name {
  font-weight: 600;
  color: #1a202c;
  font-size: 0.9rem;
  line-height: 1.25;
}

.participant-rank {
  margin-top: 0.4rem;
  font-size: 0.8rem;
  color: #6b7280;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
}

/*
  По ТЗ 03.09.2026: единый дизайн primary-кнопки как во 2 разделе КПС
  (ActionButton зелёная 56px). Радиус 10, шрифт 16.
*/
.btn-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 160px;
  height: 56px;
  padding: 0 24px;
  background: #2A8A1B;
  color: #FFFFFF;
  border: 0;
  border-radius: 10px;
  font-weight: 700;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0.3px;
  cursor: pointer;
  transition: box-shadow .2s ease, opacity .2s ease, transform .08s ease;
}
.btn-primary:hover:not(:disabled) {
  box-shadow: 0 4px 12px rgba(0,0,0,.12);
}
.btn-primary:active:not(:disabled) {
  transform: translateY(1px) scale(.99);
}
.btn-primary:disabled {
  opacity: 0.55;
  cursor: not-allowed;
  box-shadow: none;
}

.error-message {
  background: #fee;
  color: #c33;
  padding: 1rem;
  border-radius: 4px;
  margin-top: 1rem;
}

</style>
